import { APIClient } from "../helpers/api_helper";
import { ApiResponse, PaginationMeta } from "../types/api";
import { PlanCurrency } from "./planService";

const api = new APIClient();

const SUBSCRIPTION_RESOURCE = "/subscriptions/me";
const PLANS_RESOURCE = "/plans";
const CHECKOUT_RESOURCE = "/subscription/checkout";
const VALIDATE_COUPON_RESOURCE = "/subscription/checkout/validate-coupon";
const PAYMENT_METHOD_RESOURCE = "/subscription/payment-method";
const PADDLE_PORTAL_RESOURCE = "/subscription/paddle-portal";
const INVOICES_RESOURCE = "/subscription/invoices";
const TOKENIZE_CARD_RESOURCE = "/subscription/credit-card/tokenize";
const TAX_DOCUMENTS_RESOURCE = "/subscription/tax-documents";

export type TenantSubscriptionStatus =
  | "active"
  | "past_due"
  | "suspended"
  | "canceled";

export type SubscriptionPaymentMethod = "credit_card" | "pix";

export interface TenantSubscriptionPlan {
  id: number;
  name: string;
  description: string;
  currency: PlanCurrency;
  base_price: number;
  max_units: number | null;
  max_professionals: number | null;
  extra_unit_price: number;
  extra_professional_price: number;
  is_active: boolean;
}

export type AvailablePlan = TenantSubscriptionPlan;

export interface MyTenantSubscription {
  id: number;
  tenant_id: number;
  plan_id: number;
  status: TenantSubscriptionStatus;
  currency: PlanCurrency;
  gateway_slug?: string | null;
  /** txn_… (Paddle checkout) ou id da assinatura no gateway. */
  external_subscription_id?: string | null;
  payment_method?: SubscriptionPaymentMethod | string | null;
  payment_method_last4?: string | null;
  /** CPF/CNPJ do tenant (quando já cadastrado) — pré-preenche checkout PIX. */
  billing_document?: string | null;
  current_period_start: string;
  current_period_end: string;
  current_units_count: number;
  current_professionals_count: number;
  plan?: TenantSubscriptionPlan | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionCheckoutPix {
  qr_code_base64: string;
  copy_paste_string: string;
  expires_at?: string | null;
  external_payment_id?: string | null;
}

export interface CheckoutSubscriptionResponse extends ApiResponse<MyTenantSubscription> {
  pix?: SubscriptionCheckoutPix | null;
  checkout_url?: string | null;
}

export interface CouponPreview {
  coupon_code: string;
  discount_percent_bps: number;
  duration_type: string;
  duration_months: number | null;
  preview_first_invoice_cents: number;
  currency: PlanCurrency;
}

export interface CreditCardHolderInfo {
  name?: string;
  email?: string;
  cpfCnpj?: string;
  postalCode?: string;
  addressNumber?: string;
  phone?: string;
}

export interface CheckoutSubscriptionPayload {
  plan_id: number;
  payment_method?: SubscriptionPaymentMethod | "CREDIT_CARD" | "PIX" | "paddle";
  credit_card_token?: string;
  credit_card_holder_info?: CreditCardHolderInfo;
  credit_card_last4?: string;
  /** CPF (11) ou CNPJ (14) — obrigatório no PIX SaaS (Asaas). */
  payer_document?: string;
  coupon_code?: string;
}

export interface UpdatePaymentMethodPayload {
  credit_card_token: string;
  credit_card_holder_info?: CreditCardHolderInfo;
  credit_card_last4?: string;
}

export interface TokenizeCreditCardPayload {
  credit_card: {
    number: string;
    holder_name: string;
    expiry_month: string;
    expiry_year: string;
    ccv: string;
  };
  credit_card_holder_info?: CreditCardHolderInfo;
}

export interface TokenizeCreditCardResult {
  credit_card_token: string;
  credit_card_last4: string;
}

export interface SaasBillingInvoiceTaxDocument {
  id: number;
  document_type: string;
  status: string;
  document_number: string | null;
  issued_at: string | null;
  download_available: boolean;
  error_message?: string | null;
}

export interface SaasBillingInvoice {
  id: number;
  billing_period_start: string;
  billing_period_end: string;
  amount_cents: number;
  currency: PlanCurrency;
  payment_status: string;
  external_invoice_id?: string;
  paid_at: string | null;
  gateway_slug: string;
  paddle_invoice_url?: string | null;
  line_items: Array<Record<string, unknown>>;
  tax_document?: SaasBillingInvoiceTaxDocument | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceListMeta extends PaginationMeta {}

export interface InvoiceListResponse extends ApiResponse<SaasBillingInvoice[]> {
  meta?: InvoiceListMeta;
}

export interface TaxDocumentDownloadResult {
  download_url: string;
}

export const getMySubscription = (): Promise<ApiResponse<MyTenantSubscription>> =>
  api.get<ApiResponse<MyTenantSubscription>>(SUBSCRIPTION_RESOURCE);

export const getPlans = (): Promise<ApiResponse<AvailablePlan[]>> =>
  api.get<ApiResponse<AvailablePlan[]>>(PLANS_RESOURCE);

export const validateSubscriptionCoupon = (
  planId: number,
  couponCode: string
): Promise<ApiResponse<CouponPreview>> =>
  api.create<ApiResponse<CouponPreview>>(VALIDATE_COUPON_RESOURCE, {
    plan_id: planId,
    coupon_code: couponCode,
  });

export const checkout = (
  payload: CheckoutSubscriptionPayload
): Promise<CheckoutSubscriptionResponse> =>
  api.create<CheckoutSubscriptionResponse>(CHECKOUT_RESOURCE, payload);

export const getPendingPix = (): Promise<ApiResponse<SubscriptionCheckoutPix>> =>
  api.get<ApiResponse<SubscriptionCheckoutPix>>("/subscription/pending-pix");

/** Consulta o Asaas e ativa a assinatura se o PIX já estiver pago (fallback de webhook). */
export const syncSubscriptionPaymentStatus = (): Promise<
  ApiResponse<MyTenantSubscription>
> => api.create<ApiResponse<MyTenantSubscription>>("/subscription/sync-payment-status", {});

export const updateSubscriptionPaymentMethod = (
  payload: UpdatePaymentMethodPayload
): Promise<ApiResponse<MyTenantSubscription>> =>
  api.create<ApiResponse<MyTenantSubscription>>(PAYMENT_METHOD_RESOURCE, payload);

export interface PaddleCustomerPortalResult {
  portal_url: string;
}

/** Temporary authenticated Paddle Customer Portal URL (do not cache). */
export const createPaddleCustomerPortal = (): Promise<
  ApiResponse<PaddleCustomerPortalResult>
> => api.create<ApiResponse<PaddleCustomerPortalResult>>(PADDLE_PORTAL_RESOURCE, {});

export const tokenizeSubscriptionCreditCard = (
  payload: TokenizeCreditCardPayload
): Promise<ApiResponse<TokenizeCreditCardResult>> =>
  api.create<ApiResponse<TokenizeCreditCardResult>>(TOKENIZE_CARD_RESOURCE, payload);

export const getSubscriptionInvoices = (
  page = 1
): Promise<InvoiceListResponse> =>
  api.get<InvoiceListResponse>(INVOICES_RESOURCE, { params: { page, per_page: 15 } });

export const getSubscriptionInvoice = (
  invoiceId: number
): Promise<ApiResponse<SaasBillingInvoice>> =>
  api.get<ApiResponse<SaasBillingInvoice>>(`${INVOICES_RESOURCE}/${invoiceId}`);

export const getPaddleInvoiceUrl = (
  invoiceId: number
): Promise<ApiResponse<{ paddle_invoice_url: string }>> =>
  api.get<ApiResponse<{ paddle_invoice_url: string }>>(
    `${INVOICES_RESOURCE}/${invoiceId}/paddle-invoice-url`
  );

export const downloadTaxDocument = async (taxDocumentId: number): Promise<void> => {
  const response = await api.get<ApiResponse<TaxDocumentDownloadResult>>(
    `${TAX_DOCUMENTS_RESOURCE}/${taxDocumentId}/download`
  );

  if (!response.data?.download_url) {
    throw new Error("MISSING_DOWNLOAD_URL");
  }

  window.open(response.data.download_url, "_blank", "noopener,noreferrer");
};
